import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, GraduationCap, Calendar } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Education } from '../../lib/supabase';
import { ImageUpload } from './ImageUpload';

interface EducationFormData {
  school: string;
  level: string;
  start_year: string;
  end_year: string;
  description: string;
  image_url: string;
  is_current: boolean;
}

const initialFormData: EducationFormData = {
  school: '',
  level: '',
  start_year: '',
  end_year: '',
  description: '',
  image_url: '',
  is_current: false
};

export const EducationManagement: React.FC = () => {
  const [educations, setEducations] = useState<Education[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<EducationFormData>(initialFormData);

  useEffect(() => {
    fetchEducations();
  }, []);

  const fetchEducations = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('education')
        .select('*')
        .order('start_year', { ascending: false });

      if (error) throw error;
      setEducations(data || []);
    } catch (err) {
      console.error('Error fetching education:', err);
      setError('Gagal memuat data pendidikan');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof EducationFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Get profile_id from profiles table
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .single();

      if (profileError) {
        throw new Error('Gagal mendapatkan profile ID');
      }

      const educationData = {
        school: formData.school,
        level: formData.level,
        start_year: parseInt(formData.start_year),
        end_year: formData.end_year ? parseInt(formData.end_year) : null,
        description: formData.description,
        image_url: formData.image_url || null,
        profile_id: profile.id,
        is_current: formData.is_current || false
      };

      if (editingId) {
        // Update existing education
        const { error } = await supabase
          .from('education')
          .update(educationData)
          .eq('id', editingId);

        if (error) throw error;
        setSuccess('Data pendidikan berhasil diperbarui!');
      } else {
        // Create new education
        const { error } = await supabase
          .from('education')
          .insert([educationData]);

        if (error) throw error;
        setSuccess('Data pendidikan berhasil ditambahkan!');
      }

      setFormData(initialFormData);
      setShowForm(false);
      setEditingId(null);
      fetchEducations();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving education:', err);
      setError('Gagal menyimpan data pendidikan');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (education: Education) => {
    setFormData({
      school: education.school,
      level: education.level,
      start_year: education.start_year.toString(),
      end_year: education.end_year ? education.end_year.toString() : '',
      description: education.description || '',
      image_url: education.image_url || '',
      is_current: education.is_current || false
    });
    setEditingId(education.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Apakah Anda yakin ingin menghapus data pendidikan ini?')) {
      return;
    }

    try {
      setError(null);
      const { error } = await supabase
        .from('education')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setSuccess('Data pendidikan berhasil dihapus!');
      fetchEducations();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting education:', err);
      setError('Gagal menghapus data pendidikan');
    }
  };

  const handleCancel = () => {
    setFormData(initialFormData);
    setShowForm(false);
    setEditingId(null);
  };



  if (loading) {
    return (
      
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Memuat data pendidikan...</span>
        </div>
      
    );
  }

  return (
    
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Kelola Pendidikan</h1>
              <p className="mt-1 text-sm text-gray-500">
                Tambah, edit, atau hapus riwayat pendidikan yang akan ditampilkan di portofolio
              </p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Tambah Pendidikan
            </button>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="text-sm text-green-700">{success}</div>
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="mb-6 bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {editingId ? 'Edit Data Pendidikan' : 'Tambah Data Pendidikan Baru'}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="school" className="block text-sm font-medium text-gray-700 mb-2">
                    Nama Sekolah *
                  </label>
                  <input
                    type="text"
                    id="school"
                    required
                    value={formData.school}
                    onChange={(e) => handleInputChange('school', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nama sekolah/universitas"
                  />
                </div>

                <div>
                  <label htmlFor="level" className="block text-sm font-medium text-gray-700 mb-2">
                    Jenjang *
                  </label>
                  <input
                    type="text"
                    id="level"
                    required
                    value={formData.level}
                    onChange={(e) => handleInputChange('level', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="SD, SMP, SMA, S1, S2, dll"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="start_year" className="block text-sm font-medium text-gray-700 mb-2">
                    Tahun Mulai *
                  </label>
                  <input
                    type="number"
                    id="start_year"
                    required
                    min="1900"
                    max="2030"
                    value={formData.start_year}
                    onChange={(e) => handleInputChange('start_year', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="2020"
                  />
                </div>

                <div>
                  <label htmlFor="end_year" className="block text-sm font-medium text-gray-700 mb-2">
                    Tahun Selesai
                  </label>
                  <input
                    type="number"
                    id="end_year"
                    min="1900"
                    max="2030"
                    value={formData.end_year}
                    onChange={(e) => handleInputChange('end_year', e.target.value)}
                    disabled={formData.is_current}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="2024"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_current"
                  checked={formData.is_current || false}
                  onChange={(e) => {
                    const isCurrent = e.target.checked;
                    handleInputChange('is_current', isCurrent);
                    if (isCurrent) {
                      handleInputChange('end_year', '');
                    }
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_current" className="ml-2 block text-sm text-gray-700">
                  Masih bersekolah disini
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gambar Sekolah
                </label>
                <ImageUpload
                  value={formData.image_url}
                  onChange={(url) => handleInputChange('image_url', url)}
                  bucket="portfolio-images"
                  folder="education"
                  accept="image/*"
                  maxSize={5}
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Deskripsi
                </label>
                <textarea
                  id="description"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Prestasi, aktivitas, atau catatan khusus..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <X className="h-4 w-4 mr-2" />
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={saving || !formData.school || !formData.level || !formData.start_year}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {editingId ? 'Perbarui' : 'Simpan'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Education List */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Riwayat Pendidikan ({educations.length})
            </h3>
          </div>

          {educations.length === 0 ? (
            <div className="p-6 text-center">
              <GraduationCap className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Belum ada data pendidikan</h3>
              <p className="mt-1 text-sm text-gray-500">
                Mulai dengan menambahkan riwayat pendidikan pertama.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Tambah Pendidikan
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sekolah
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Jenjang
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Periode
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Aksi
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {educations.map((education) => (
                      <tr key={education.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            {education.image_url && (
                              <img
                                src={education.image_url}
                                alt={education.school}
                                className="h-10 w-10 rounded-lg object-cover mr-3"
                              />
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {education.school}
                              </div>
                              {education.description && (
                                <div className="text-sm text-gray-500">
                                  {education.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {education.level}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            <div>
                              {education.start_year}
                              {education.end_year ? (
                                <> - {education.end_year}</>
                              ) : (
                                education.is_current && <> - Sekarang</>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            education.is_current 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {education.is_current ? 'Sedang Berlangsung' : 'Selesai'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleEdit(education)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(education.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    
  );
};